import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { Store } from '@ngxs/store';
import { toSignal } from '@angular/core/rxjs-interop';
import { AuthState } from '../core/authentication/auth.state';

interface DashboardItem {
  labelKey: string
  route: string[]
  icon: string
  roles?: string[]
}

interface DashboardGroup {
  titleKey: string
  icon: string
  items: DashboardItem[]
}

@Component({
  selector: 'pixl-home',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TranslocoModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DashboardComponent {
  private readonly store = inject(Store);

  private readonly editorSignal = toSignal(
    this.store.select(AuthState.getEditor),
    { initialValue: null }
  );

  private readonly allDashboardGroups: DashboardGroup[] = [
    {
      titleKey: 'dashboard.group.learning_contents',
      icon: 'category',
      items: [
        { labelKey: 'dashboard.links.exam_sequences', route: ['/lerninhalte', 'sequenzen', 'examSequence'], icon: 'snowshoeing' },
        { labelKey: 'dashboard.links.exercise_sequences', route: ['/lerninhalte', 'sequenzen', 'exerciseSequence'], icon: 'sports_gymnastics' },
        { labelKey: 'dashboard.links.exam_tasks', route: ['/lerninhalte', 'aufgaben', 'exam'], icon: 'quiz' },
        { labelKey: 'dashboard.links.exercise_tasks', route: ['/lerninhalte', 'aufgaben', 'exercise'], icon: 'edit_square' },
        { labelKey: 'dashboard.links.explanations', route: ['/lerninhalte', 'explanation'], icon: 'privacy_tip' },
        { labelKey: 'dashboard.links.feedback', route: ['/lerninhalte', 'feedback'], icon: 'feedback' },
        { labelKey: 'dashboard.links.tests', route: ['/lerninhalte', 'test'], icon: 'detection_and_zone' },
        { labelKey: 'dashboard.links.help', route: ['/lerninhalte', 'functionalHelp'], icon: 'help_center' }
      ]
    },
    {
      titleKey: 'dashboard.group.digital_modules',
      icon: 'gradient',
      items: [
        { labelKey: 'dashboard.links.modules', route: ['/lerninhalte', 'dig-lernmodule', 'modules'], icon: 'view_module' },
        { labelKey: 'dashboard.links.units', route: ['/lerninhalte', 'dig-lernmodule', 'learning-units'], icon: 'layers' },
        { labelKey: 'dashboard.links.sections', route: ['/lerninhalte', 'dig-lernmodule', 'learning-sections'], icon: 'line_style' }
      ]
    },
    {
      titleKey: 'dashboard.group.administration',
      icon: 'admin_panel_settings',
      items: [
        { labelKey: 'dashboard.links.consumers', route: ['/consumers'], icon: 'web_asset', roles: ['zavAdmin'] },
        { labelKey: 'dashboard.links.products', route: ['/products'], icon: 'add_shopping_cart', roles: ['editor', 'appAdmin', 'zavAdmin'] },
        { labelKey: 'dashboard.links.metadata', route: ['/global-metadata'], icon: 'public', roles: ['zavAdmin'] },
        { labelKey: 'dashboard.links.users', route: ['/editors'], icon: 'group', roles: ['zavAdmin'] },
        { labelKey: 'dashboard.links.trees', route: ['/generic-trees'], icon: 'account_tree', roles: ['editor', 'appAdmin', 'zavAdmin'] },
        { labelKey: 'dashboard.links.subjects', route: ['/subjects'], icon: 'work', roles: ['zavAdmin'] }
      ]
    }
  ];

  public readonly filteredDashboardGroups = computed<DashboardGroup[]>(() => {
    const editor = this.editorSignal();
    const userRoles = editor?.role ? [editor.role] : [];

    return this.allDashboardGroups
      .map(group => {
        const visibleItems = group.items.filter(item => {
          if (!item.roles || item.roles.length === 0) {
            return true;
          }
          return item.roles.some(role => userRoles.includes(role));
        });

        return { ...group, items: visibleItems };
      })
      .filter(group => group.items.length > 0);
  });
}
